import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import classnames from 'classnames';

import BixToggleCellRenderer from '$/components/global/bixToggleCellRenderer';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import {
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import { getHasBixbyComplianceFeatureFlagState, getHasPublicCompaniesFeatureFlagState } from '$/store/login/selectors';
import BixToggle from '$/components/global/bixToggle';
import SettingsNavigation from '../SettingsNavigation';
import { AccountService } from '$/services/AccountService';
import { toastError } from '$/store/toast/actions';
import { ReactComponent as Padlock } from '$/assets/icons/padlock.svg';
import './index.scss';

interface IMyCompaniesProps {
  isAdmin: boolean;
  isClient: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
  hasPublicCompaniesFeatureFlag: boolean;
  hasBixbyComplianceFeatureFlag: boolean;
  dispatchToastError: (message: string) => Promise<void>;
}

const MyCompanies: React.FC<IMyCompaniesProps> = props => {
  const {
    isAdmin,
    isClient,
    isCreditSightsOpsSufOrRatings,
    hasPublicCompaniesFeatureFlag,
    hasBixbyComplianceFeatureFlag,
    dispatchToastError,
  } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isAllPublicCompanyToggleLoading, setIsAllPublicCompanyToggleLoading] = useState(false);
  const [isAllPrivateCompanyToggleLoading, setIsAllPrivateCompanyToggleLoading] = useState(false);
  const [companies, setCompanies] = useState([] as Bixby.Models.AccountUserCompany[]);
  const [isLoading, setIsLoading] = useState(true);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const getRowId = useCallback(params => {
    return params.data.company_Id;
  }, []);

  const handleFetchCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      const companyResults = await AccountService.getCompanies();
      setCompanies(companyResults);
    } catch (err) {
      dispatchToastError('Unable to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [dispatchToastError]);

  useEffect(() => {
    if (isClient === true && handleFetchCompanies) {
      handleFetchCompanies();
    }
  }, [isClient, handleFetchCompanies]);

  const handleStateChange = (id, newCheckedState, property) => {
    const newValues = companies.map(existingValue => {
      if (existingValue.company_Id === id) {
        existingValue[property] = newCheckedState;
      }

      return existingValue;
    });

    setCompanies(newValues);
  };

  const updateAlertState = async (id, newCheckedState) => {
    handleStateChange(id, newCheckedState, 'alertsEnabled');
    await AccountService.updateAccountUserCompanyAlertState(id, newCheckedState);
  };

  const updateMyPortfolioState = async (id, newCheckedState) => {
    handleStateChange(id, newCheckedState, 'myPortfolioEnabled');
    await AccountService.updateAccountUserCompanyMyPortfolioState(id, newCheckedState);
  };

  const allPublicCompaniesHaveAlerts = useMemo(
    () => companies && companies.filter(_ => _.isSECPublic).every(c => c.alertsEnabled),
    [companies]
  );

  const allPrivateCompaniesHaveAlerts = useMemo(
    () => companies && companies.filter(_ => !_.isSECPublic).every(c => c.alertsEnabled),
    [companies]
  );

  const updateAllAlerts = async (newCheckedState, isSECPublic) => {
    try {
      if (isSECPublic) {
        setIsAllPublicCompanyToggleLoading(true);
      } else {
        setIsAllPrivateCompanyToggleLoading(true);
      }

      await AccountService.updateAccountAllUserCompaniesAlertState(newCheckedState, isSECPublic);

      const newValues = companies.map(existingValue => {
        if ((isSECPublic && existingValue.isSECPublic) || (!isSECPublic && !existingValue.isSECPublic)) {
          existingValue.alertsEnabled = newCheckedState;
        }

        return existingValue;
      });

      setCompanies(newValues);
    } catch (e: any) {
      // suppress
    } finally {
      if (isSECPublic) {
        setIsAllPublicCompanyToggleLoading(false);
      } else {
        setIsAllPrivateCompanyToggleLoading(false);
      }
    }
  };

  const content = isAdmin ? (
    <React.Fragment>
      <section>
        <div className="user-companies-section">
          <h3>Admin User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isLoading ? (
    <Loading pageLevel />
  ) : isClient ? (
    <React.Fragment>
      <section>
        <div className="user-companies-note">
          <p>Individual company choices override sector and industry group choices.</p>
        </div>
        <div className="user-companies-section">
          {Bixby.Utils.isNullOrEmpty(companies) && <NoDataFound dataType="companies" />}
          {!Bixby.Utils.isNullOrEmpty(companies) && (
            <>
              {hasPublicCompaniesFeatureFlag && (
                <>
                  <div className="toggle-wrap">
                    <BixToggle
                      disabled={isAllPublicCompanyToggleLoading}
                      text={`Turn ${allPublicCompaniesHaveAlerts ? 'off' : 'on'} alerts for Public Companies`}
                      id="all-public-companies-have-alerts"
                      checked={allPublicCompaniesHaveAlerts}
                      onChange={async (newCheckedState: boolean) => {
                        await updateAllAlerts(newCheckedState, true);
                      }}
                    />
                    {isAllPublicCompanyToggleLoading && <Loading inline />}
                  </div>
                  <div className="toggle-wrap">
                    <BixToggle
                      disabled={isAllPrivateCompanyToggleLoading}
                      text={`Turn ${allPrivateCompaniesHaveAlerts ? 'off' : 'on'} alerts for Private Companies`}
                      id="all-private-companies-have-alerts"
                      checked={allPrivateCompaniesHaveAlerts}
                      onChange={async (newCheckedState: boolean) => {
                        await updateAllAlerts(newCheckedState, false);
                      }}
                    />
                    {isAllPrivateCompanyToggleLoading && <Loading inline />}
                  </div>
                </>
              )}
              <div
                className={classnames('user_companies_data_grid', {
                  with_public_companies: hasPublicCompaniesFeatureFlag,
                })}>
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      enableRangeSelection={false}
                      getContextMenuItems={getContextMenuItems}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        sortable: true,
                        resizable: true,
                        lockPinned: true,
                      }}
                      onGridReady={onGridReady}
                      rowData={companies}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      suppressCellFocus={true}
                      animateRows={true}
                      getRowId={getRowId}
                      rowHeight={32}
                      headerHeight={32}
                      components={{
                        bixToggleCellRenderer: BixToggleCellRenderer,
                      }}
                      columnDefs={[
                        {
                          field: 'company_Name',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          headerName: 'Company',
                          cellRenderer: (params: any) => {
                            return (
                              <div className="company_contain">
                                <a className="link_underline" href={`/company/${params.data['company_Id']}`}>
                                  {params.data['company_Name']}
                                </a>
                                {hasBixbyComplianceFeatureFlag &&
                                  params.data.declarationType === Bixby.Enums.DeclarationType.Private && (
                                    <div className="private-company" role="presentation" title="Private Declaration">
                                      <Padlock />
                                    </div>
                                  )}
                              </div>
                            );
                          },
                          minWidth: 250,
                          pinned: 'left',
                        },
                        {
                          field: 'sector_Name',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          headerName: 'Sector',
                          minWidth: 250,
                          cellClass: 'sector',
                        },
                        {
                          field: 'industryGroup_Name',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          headerName: 'Industry Group',
                          minWidth: 250,
                          cellClass: 'industry-group',
                        },
                        {
                          field: 'isSECPublic',
                          hide: !hasPublicCompaniesFeatureFlag,
                          headerName: 'Ownership',
                          minWidth: 130,
                          cellClass: 'align-left',
                          cellRenderer: (params: any) => (params.data['isSECPublic'] ? 'Public' : 'Private'),
                          filter: 'agTextColumnFilter',
                          filterParams: {
                            valueGetter: (params: any) => (params.data['isSECPublic'] ? 'Public' : 'Private'),
                          },
                        },
                        {
                          field: 'alertsEnabled',
                          headerName: 'Email Alerts',
                          minWidth: 100,
                          cellClass: 'align-center',
                          cellRenderer: 'bixToggleCellRenderer',
                          cellRendererParams: {
                            checkedValue: 'alertsEnabled',
                            idValue: 'company_Id',
                            getComponentId: (params: ICellRendererParams) => {
                              return `${params.data['company_Id']}.alertsEnabled`;
                            },
                            handleToggle: (companyId: string, newCheckedState: boolean) => {
                              updateAlertState(companyId, newCheckedState);
                            },
                          },
                        },
                        {
                          field: 'myPortfolioEnabled',
                          headerName: 'MyPortfolio',
                          minWidth: 100,
                          hide: isCreditSightsOpsSufOrRatings,
                          cellClass: 'align-center',
                          cellRenderer: 'bixToggleCellRenderer',
                          cellRendererParams: {
                            checkedValue: 'myPortfolioEnabled',
                            idValue: 'company_Id',
                            getComponentId: (params: ICellRendererParams) => {
                              return `${params.data['company_Id']}.myPortfolioEnabled`;
                            },
                            handleToggle: (companyId: string, newCheckedState: boolean) => {
                              updateMyPortfolioState(companyId, newCheckedState);
                            },
                          },
                        },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </React.Fragment>
  ) : null;
  return <SettingsNavigation>{content}</SettingsNavigation>;
};

const mapStateToProps = (state: AppState) => {
  const { isClient } = getIsCurrentUserClientState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { hasPublicCompaniesFeatureFlag } = getHasPublicCompaniesFeatureFlagState(state);
  const { hasBixbyComplianceFeatureFlag } = getHasBixbyComplianceFeatureFlagState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);

  return {
    isAdmin,
    isClient,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
    hasPublicCompaniesFeatureFlag,
    hasBixbyComplianceFeatureFlag,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyCompanies);
